import {Tooltip} from '@mui/material'
import {Box, Button, Checkbox, DropdownItem, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useCallback, useMemo, useState} from 'react'
import AppAddButton from 'src/components/app-add-button/AppAddButton'
import Dropdown from 'src/components/dropdown/Dropdown'
import useCameras from 'src/hooks/api/camera/useCameras'
import useDeleteLayout from 'src/hooks/api/layouts/useDeleteLayout'
import useLayouts from 'src/hooks/api/layouts/useLayouts'
import useUpdateLayout from 'src/hooks/api/layouts/useUpdateLayout'
import DeleteIcon from 'src/images/DeleteIcon'
import {EditPenIcon} from 'src/images/EditPenIcon'
import SaveIcon from 'src/images/SaveIcon'
import {ContainerPaperContent} from 'src/layouts/main-layout/LayoutBase'

import useWebsocket from 'src/hooks/api/socket/useWebsocket'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import CamerasList from 'src/pages/camera-mosaic/components/CamerasList'
import DetectionsList, {DetectionsToDisplay} from 'src/pages/camera-mosaic/components/DetectionsList'
import LayoutDefault from 'src/pages/camera-mosaic/components/LayoutDefault'
import LayoutRegisterModal from 'src/pages/camera-mosaic/components/LayoutRegisterModal'
import {FaceDetection} from 'src/services/api'
import {Layout} from 'src/services/api/endpoints/LayoutEndpoint'
import {Lpr} from 'src/services/api/endpoints/LprEndpoint'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {pickStrongerColor} from 'src/utilities/pickStrongerColor'

export enum LayoutTypes {
  Default = 1,
  LPR = 2,
  Facial = 3,
}

const CameraMosaic = () => {
  const [savedLayoutSelected, setSavedLayoutSelected] = useState<Layout>()

  const [isCreate, setIsCreate] = useState<boolean>(true)
  const [openCreateEditModal, setOpenCreateEditModal] = useState<boolean>(false)

  const {enqueueSnackbar} = useSnackbar()

  const {selectedCompanies} = useCompaniesSelector()

  const {
    data: layouts,
    isLoading: loadingLayouts,
    refetch,
  } = useLayouts({
    companyIds: selectedCompanies.map((it) => it.id),
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Não foi possível carregar a listagem de layouts'),
  })

  const {mutateAsync: deleteLayout} = useDeleteLayout()
  const {mutateAsync: updateLayout} = useUpdateLayout()

  const {
    data: cameras,
    isLoading: isLoadingCameras,
    isRefetching: isRefetchingCamera,
  } = useCameras({
    selectedCompanies: selectedCompanies.map((it) => it.id),
    originsToFilter: ['RTMP', 'RTSP', 'RTSP-DVR'],
    clientIds: savedLayoutSelected && savedLayoutSelected.clientId ? [savedLayoutSelected.clientId] : undefined,
    includes: ['company', 'client', 'configuration'],
  })
  const [camerasIdsSelected, setCamerasIdsSelected] = useState<number[]>([])

  const camerasSelected = useMemo(() => cameras?.data.data.entities.filter((cam) => camerasIdsSelected.includes(cam.id)) ?? [], [camerasIdsSelected, cameras])

  const [detectionsToDisplay, setDetectionsToDisplay] = useState<DetectionsToDisplay[]>([])

  const onFaceMessage = useCallback(
    (event: FaceDetection) => {
      if (!camerasSelected.some((camera) => camera.id === event.cameraId)) return

      const camera = camerasSelected.find((camera) => camera.id === event.cameraId)
      const date = new Date(event.createdAt).toLocaleString('pt-br', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      const actionsColor = event.activatedActions
        .map((actions) => actions.actionColor)
        .filter((color): color is string => color !== undefined && color !== null)
      const color = pickStrongerColor(actionsColor)

      const detection: DetectionsToDisplay = {
        id: event.id,
        createdAt: event.createdAt,
        croppedSrc: event.cropImageKey,
        imageSrc: event.imageKey,
        infos: [
          {title: 'Nome', value: event.face?.name ?? 'Desconhecido'},
          {title: 'Câmera', value: camera?.name ?? 'Desconhecida'},
          {title: 'Data', value: date},
        ],
        actionColor: color,
      }

      setDetectionsToDisplay((prev) => {
        const newDetections = [...prev, detection]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))

        return newDetections
      })
    },
    [camerasSelected],
  )

  useWebsocket({
    listenTo: [FaceDetectionWSChannelEnum.FACE_DETECTION],
    room: WebSocketRoomTypeEnum.FACE,
    onMessage: onFaceMessage,
    enable: savedLayoutSelected?.layoutTypeId === LayoutTypes.Facial,
  })

  const onPlateMessage = useCallback(
    (event: Lpr) => {
      if (!camerasSelected.some((camera) => camera.id === event.camera.id)) return

      const camera = camerasSelected.find((camera) => camera.id === event.camera.id)
      const date = new Date(event.createdAt).toLocaleString('pt-br', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })

      const detection: DetectionsToDisplay = {
        id: event.id,
        createdAt: event.createdAt,
        croppedSrc: event.croppedImageAwsKey,
        imageSrc: event.imageAwsKey,
        infos: [
          {title: 'Placa', value: event.plate.licensePlate},
          {title: 'Câmera', value: camera?.name ?? 'Desconhecida'},
          {title: 'Data', value: date},
        ],
        actionColor: undefined,
      }

      setDetectionsToDisplay((prev) => {
        const newDetections = [...prev, detection]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))

        return newDetections
      })
    },
    [camerasSelected],
  )

  useWebsocket({
    room: WebSocketRoomTypeEnum.LPR,
    listenTo: [FaceDetectionWSChannelEnum.LPR_DETECTION],
    onMessage: onPlateMessage,
    enable: savedLayoutSelected?.layoutTypeId === LayoutTypes.LPR,
  })

  const handleAddOrRemoveCam = (id: number) => {
    const exists = camerasIdsSelected.some((v) => v === id)
    if (!exists && camerasIdsSelected.length >= 16) return enqueueSnackbar('Quantidade de câmeras no layout atingida', {variant: 'info'})

    setCamerasIdsSelected((prev) => {
      if (exists) return prev.filter((v) => v !== id)
      return [...prev, id]
    })
  }

  const handleDeleteLayout = async () => {
    if (!savedLayoutSelected) return
    await deleteLayout([savedLayoutSelected.id])
    setShowSelectedLayout(undefined)
    setSavedLayoutSelected(undefined)
  }

  const [showSelectedLayout, setShowSelectedLayout] = useState<DropdownItem>()

  const {styledTheme} = usePersonalizedThemeByCompany()

  const [showBarriers, setShowBarriers] = useState(false)

  const [tabSelected, setTabSelected] = useState<'camera' | 'detections'>('detections')
  const tabs: Array<{id: 'camera' | 'detections'; label: string}> = [
    {id: 'camera', label: 'Câmeras'},
    {id: 'detections', label: 'Detecções'},
  ]

  return (
    <ContainerPaperContent style={{display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', minHeight: 0}}>
      <Box display='flex' height='auto' padding='20px 16px 10px' justifyContent='space-between' direction='column' style={{flexShrink: 0}}>
        <Box display='flex' justifyContent='space-between'>
          <Box display='flex' align='center' justifyContent='center' columnGap='20px'>
            <Dropdown
              items={
                layouts?.map((layout) => {
                  let label = ''
                  if (layout.layoutTypeId === LayoutTypes.Default) label = `Padrão - ${layout.name}`
                  if (layout.layoutTypeId === LayoutTypes.LPR) label = `LPR - ${layout.name}`
                  if (layout.layoutTypeId === LayoutTypes.Facial) label = `Facial - ${layout.name}`

                  return {
                    id: String(layout.id),
                    label,
                  }
                }) ?? []
              }
              selected={showSelectedLayout}
              onChangeSelected={(e) => {
                setShowSelectedLayout(e)
                setDetectionsToDisplay([])
                setSavedLayoutSelected(layouts?.find((lay) => lay.id === Number(e.id)))
                const cam = layouts?.find((lay) => lay.id === Number(e.id))?.cameras?.map((cam) => cam.cameraId)
                setCamerasIdsSelected(cam ?? [])
              }}
              key={showSelectedLayout?.id}
              placeholder='Layouts salvos'
              fontSize='12px'
              height='40px'
              width='250px'
              isLoading={loadingLayouts}
            />
            {savedLayoutSelected?.id && (
              <Box
                display='flex'
                columnGap='5px'
                style={{border: `1px solid ${styledTheme.colors.primary}`}}
                padding='3px'
                borderRadius='8px'
                boxSizing='border-box'>
                <Tooltip title='Editar layout salvo' disableTouchListener>
                  <Button
                    variant='contained'
                    hoverColor={styledTheme.hoverColors.secondary}
                    color='transparent'
                    height='32px'
                    fontSize='12px'
                    onClick={() => {
                      setIsCreate(false)
                      setOpenCreateEditModal(true)
                    }}>
                    <EditPenIcon />
                  </Button>
                </Tooltip>

                <Tooltip title='Deletar layout' disableTouchListener>
                  <Button
                    color='transparent'
                    hoverColor={styledTheme.hoverColors.secondary}
                    variant='contained'
                    height='32px'
                    fontSize='12px'
                    onClick={() => handleDeleteLayout()}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>

                <Tooltip title='Salvar câmeras no layout' disableTouchListener>
                  <Button
                    hoverColor={styledTheme.hoverColors.secondary}
                    color='transparent'
                    variant='contained'
                    height='32px'
                    fontSize='12px'
                    onClick={() =>
                      updateLayout({
                        id: savedLayoutSelected.id,
                        camerasIds: camerasIdsSelected,
                        body: {maxCameras: camerasIdsSelected.length, clientId: savedLayoutSelected.clientId},
                      })
                    }>
                    <SaveIcon />
                  </Button>
                </Tooltip>
              </Box>
            )}
            <Checkbox label='Exibir barreiras' labelSize='12px' checked={showBarriers} onChange={() => setShowBarriers((prev) => !prev)} />
          </Box>

          <Box display='flex' align='center' justifyContent='center' columnGap='20px'>
            <AppAddButton
              onClick={() => {
                setIsCreate(true)
                setOpenCreateEditModal(true)
              }}
              text='Salvar novo layout'
              sx={{width: '262px'}}
              disabled={!camerasIdsSelected.length || !!savedLayoutSelected?.id}
            />
          </Box>
        </Box>
      </Box>

      <Box display='flex' paddingLeft='16px' style={{flex: 1, minHeight: 0, alignItems: 'stretch'}}>
        <Box width='85%' marginBottom='5px' style={{display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1}}>
          <LayoutDefault cameras={camerasSelected} layoutType={savedLayoutSelected?.layoutTypeId ?? LayoutTypes.Default} showBarriers={showBarriers} />
        </Box>

        <Box
          width='15%'
          display='flex'
          padding='0 10px 10px 10px'
          align='center'
          direction='column'
          overflow='auto'
          borderRadius='8px'
          margin='0 5px'
          rowGap='5px'
          position='relative'
          style={{minHeight: 0}}>
          {savedLayoutSelected ? (
            <Box display='flex' direction='column' width='100%' rowGap='5px' align='center'>
              <Box
                height={'40px'}
                borderRadius={'8px'}
                display='flex'
                marginBottom='10px'
                align='center'
                justifyContent='center'
                width='100%'
                border='1px solid #D0D5DD'>
                {tabs.map((tab) => {
                  return (
                    <Box
                      display='flex'
                      direction='column'
                      align='center'
                      width='50%'
                      justifyContent='center'
                      borderRadius={'4px'}
                      padding={'6px 16px'}
                      margin='5px'
                      onClick={() => setTabSelected(tab.id)}
                      backgroundColor={tabSelected === tab.id ? styledTheme.colors.primary : styledTheme.colors.white}
                      style={{cursor: 'pointer'}}>
                      <Typography
                        variant='label'
                        color={tabSelected === tab.id ? styledTheme.colors.white : '#98A2B3'}
                        weight={500}
                        size='14px'
                        cursor='pointer'
                        onClick={() => setTabSelected(tab.id)}>
                        {tab.label}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>

              {tabSelected === 'camera' && (
                <CamerasList
                  cameras={cameras?.data.data.entities ?? []}
                  camerasIdsSelected={camerasIdsSelected}
                  handleAddOrRemoveCam={handleAddOrRemoveCam}
                  isLoading={isLoadingCameras || isRefetchingCamera}
                />
              )}

              {tabSelected === 'detections' &&
                detectionsToDisplay.map((it) => <DetectionsList layoutTypeId={savedLayoutSelected?.layoutTypeId ?? 1} detections={it} key={it.id} />)}
            </Box>
          ) : (
            <CamerasList
              cameras={cameras?.data.data.entities ?? []}
              camerasIdsSelected={camerasIdsSelected}
              handleAddOrRemoveCam={handleAddOrRemoveCam}
              isLoading={isLoadingCameras || isRefetchingCamera}
            />
          )}
        </Box>
      </Box>

      {openCreateEditModal && (
        <LayoutRegisterModal
          isCreate={isCreate}
          openModal={openCreateEditModal}
          closeModal={() => setOpenCreateEditModal(false)}
          layout={savedLayoutSelected || ({} as Layout)}
          setLayout={setSavedLayoutSelected}
          loadLayouts={refetch}
          camerasIdsSelected={camerasIdsSelected}
        />
      )}
    </ContainerPaperContent>
  )
}

export default CameraMosaic
